/**
 * Client-side scene store.
 *
 * Holds all mutable VTT state the canvas + tools need:
 *   - elements      — the element dict keyed by id
 *   - selectedIds   — current selection (set of ids)
 *   - tool          — active tool + per-tool options
 *   - undo/redo     — inverse-op stacks for local undo
 *
 * Deliberately not using Zustand or Redux. The canvas path is hot — we do
 * not want to rerender React on every pointer-move. Instead the store is a
 * plain object with a tiny `subscribe(listener)` that the canvas uses to
 * schedule a rAF redraw, and that small UI bits (tool palette, status bar)
 * use to rerender themselves via `useSyncExternalStore`.
 *
 * Mutations come through a narrow set of verbs:
 *   - upsertLocal(element)     — local edit, pushes onto undo stack
 *   - applyRemote(element)     — wire op; uses reconcile(); never touches undo
 *   - setTool / setSelected    — UI state
 *   - undo / redo              — walk the inverse stacks
 *
 * Wire-op broadcast is NOT handled here. Stage E introduces a thin bridge
 * that subscribes to "local mutation" events and forwards them to the
 * websocket. Keeping the store transport-agnostic means we can unit-test
 * tools without mocking a socket.
 */

import { nanoid } from "nanoid";
import {
  reconcile,
  shouldApply,
  nextStamp,
  type SceneElement,
  type SceneLayer,
} from "../../lib/scene";

// ---------------------------------------------------------------------------
// Tool state
// ---------------------------------------------------------------------------

export type ToolKind =
  | "select"
  | "pen"
  | "rect"
  | "ellipse"
  | "line"
  | "arrow"
  | "text";

/** Per-tool knobs the palette can tweak. */
export interface ToolOptions {
  strokeColor: string;
  backgroundColor: string | null;
  strokeWidth: number;
  /** Default layer new elements land on. Usually matches player role. */
  activeLayer: SceneLayer;
}

export const DEFAULT_TOOL_OPTIONS: ToolOptions = {
  strokeColor: "#f5c77a", // amber — matches --sheet-a
  backgroundColor: null,
  strokeWidth: 2,
  activeLayer: "shared",
};

// ---------------------------------------------------------------------------
// Undo entries
// ---------------------------------------------------------------------------

/**
 * Each undo entry remembers the element state *before* and *after* a local
 * mutation. Undo re-applies the `before` snapshot (or marks it deleted if
 * the element didn't exist pre-change). We stamp a fresh version on the
 * replay so the rest of the system treats it as a real edit and rebroadcasts.
 */
interface UndoEntry {
  elementId: string;
  before: SceneElement | null;
  after: SceneElement;
}

// ---------------------------------------------------------------------------
// Store shape
// ---------------------------------------------------------------------------

export interface SceneStoreState {
  mapId: string | null;
  /** The current viewer's user id (Cognito sub). Needed to stamp ownerId. */
  userId: string;
  /** Whether this viewer is the DM. Controls which layers they can write. */
  isDm: boolean;

  elements: Record<string, SceneElement>;
  selectedIds: Set<string>;

  tool: ToolKind;
  toolOptions: ToolOptions;

  /**
   * Layers currently shown in this viewer's canvas. The server has already
   * stripped layers this user can't see (e.g. DM layer for players), so
   * this is purely a local DM-facing toggle: "hide player-layer clutter
   * while I'm running a boss fight". Defaults to all three visible.
   */
  visibleLayers: Set<SceneLayer>;
}

type Listener = () => void;

export class SceneStore {
  private state: SceneStoreState;
  private listeners = new Set<Listener>();
  private undoStack: UndoEntry[] = [];
  private redoStack: UndoEntry[] = [];
  /**
   * Ids that have been locally authored (upsertLocal / undo / redo)
   * since the wire bridge last drained. The SceneClient consumes this
   * in its subscription handler so we know exactly which changes to
   * broadcast — comparing `ownerId === userId` doesn't work because a
   * DM editing a player-owned element would never broadcast.
   */
  private localDirty = new Set<string>();
  /**
   * Monotonic counter the canvas uses to decide whether a notify
   * actually requires re-painting the static layer. Bumps on changes
   * that affect what the static pass draws — elements, selection,
   * visible layers — but NOT on tool / toolOptions changes, which only
   * affect the live preview.
   */
  private sceneVersion = 0;
  /**
   * Monotonic counter that bumps on EVERY notify, regardless of what
   * changed. Small UI components (`ToolPalette`, `LayerPicker`, etc.)
   * use this as their `useSyncExternalStore` snapshot — a primitive that
   * React can compare with `Object.is`. Without it the components would
   * be reading the stable `getState()` reference and React would short-
   * circuit the re-render, leaving the UI stale until something else
   * incidentally re-rendered the parent.
   */
  private notifyVersion = 0;

  /** Maximum undo history we retain. Games run for hours — bound this. */
  static readonly MAX_UNDO = 200;

  constructor(init: Partial<SceneStoreState> = {}) {
    this.state = {
      mapId: null,
      userId: "",
      isDm: false,
      elements: {},
      selectedIds: new Set(),
      tool: "select",
      toolOptions: { ...DEFAULT_TOOL_OPTIONS },
      visibleLayers: new Set<SceneLayer>(["dm", "shared", "player"]),
      ...init,
    };
  }

  // ---- read ----

  getState(): SceneStoreState {
    return this.state;
  }

  getElement(id: string): SceneElement | undefined {
    return this.state.elements[id];
  }

  /** Ordered iteration. Insertion order is the z-order: later = on top. */
  listElements(): SceneElement[] {
    return Object.values(this.state.elements).filter((e) => !e.isDeleted);
  }

  /**
   * Same as {@link listElements} but additionally filtered by the current
   * layer-visibility toggle. Use this for rendering; use listElements for
   * persistence, selection walks, or anything else that must not depend
   * on the viewer's toggle state.
   */
  listVisibleElements(): SceneElement[] {
    const { visibleLayers } = this.state;
    return Object.values(this.state.elements).filter(
      (e) => !e.isDeleted && visibleLayers.has(e.layer),
    );
  }

  // ---- subscriptions ----

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.notifyVersion++;
    // Copy listeners defensively — a listener may unsubscribe during dispatch.
    for (const l of [...this.listeners]) l();
  }

  /** Read the current static-pass version (see `sceneVersion`). */
  getSceneVersion(): number {
    return this.sceneVersion;
  }

  /**
   * Read the per-notify monotonic counter. UI components pass this as
   * their `useSyncExternalStore` snapshot so React actually re-renders
   * on tool / option / selection / element changes.
   */
  getNotifyVersion(): number {
    return this.notifyVersion;
  }

  private bumpSceneVersion(): void {
    this.sceneVersion++;
  }

  // ---- tool / selection UI ----

  setTool(tool: ToolKind): void {
    if (this.state.tool === tool) return;
    this.state.tool = tool;
    // Switching tools clears the marquee selection so pen mode doesn't keep
    // drag handles hanging around from a previous select.
    if (tool !== "select") this.state.selectedIds = new Set();
    this.notify();
  }

  setToolOptions(patch: Partial<ToolOptions>): void {
    this.state.toolOptions = { ...this.state.toolOptions, ...patch };
    this.notify();
  }

  setSelected(ids: Iterable<string>): void {
    this.state.selectedIds = new Set(ids);
    this.bumpSceneVersion();
    this.notify();
  }

  setMap(mapId: string): void {
    if (this.state.mapId === mapId) return;
    this.state.mapId = mapId;
    this.state.elements = {};
    this.state.selectedIds = new Set();
    this.undoStack = [];
    this.redoStack = [];
    this.localDirty.clear();
    this.bumpSceneVersion();
    this.notify();
  }

  setIdentity(userId: string, isDm: boolean): void {
    this.state.userId = userId;
    this.state.isDm = isDm;
    if (!isDm && this.state.toolOptions.activeLayer === "dm") {
      this.state.toolOptions.activeLayer = "shared";
    }
    // Players should never have the DM toggle in their visible set — the
    // server wouldn't send DM elements anyway, but keep local state honest.
    if (!isDm) this.state.visibleLayers.delete("dm");
    this.bumpSceneVersion();
    this.notify();
  }

  /** Toggle whether a layer is rendered on this viewer's canvas. */
  setLayerVisible(layer: SceneLayer, visible: boolean): void {
    const next = new Set(this.state.visibleLayers);
    if (visible) next.add(layer);
    else next.delete(layer);
    this.state.visibleLayers = next;
    // Drop any selected element whose layer just disappeared, so undo /
    // delete shortcuts don't secretly act on something off-screen.
    if (!visible) {
      const elements = this.state.elements;
      const pruned = new Set<string>();
      for (const id of this.state.selectedIds) {
        const el = elements[id];
        if (el && el.layer !== layer) pruned.add(id);
      }
      this.state.selectedIds = pruned;
    }
    this.bumpSceneVersion();
    this.notify();
  }

  // ---- element mutation ----

  /**
   * Build a blank element of `type` on the active layer, owned by the local
   * user, with initial version stamps. Callers finish populating the shape
   * (geometry, style, points, text) and pass the result to {@link upsertLocal}.
   */
  createElement(type: SceneElement["type"], overrides: Partial<SceneElement> = {}): SceneElement {
    const { mapId, userId, toolOptions } = this.state;
    if (!mapId) throw new Error("No map set — cannot create element");
    const stamp = nextStamp(0);
    return {
      id: nanoid(14),
      mapId,
      type,
      ownerId: userId,
      layer: toolOptions.activeLayer,
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      angle: 0,
      strokeColor: toolOptions.strokeColor,
      backgroundColor: toolOptions.backgroundColor,
      strokeWidth: toolOptions.strokeWidth,
      strokeStyle: "solid",
      fillStyle: toolOptions.backgroundColor ? "solid" : "none",
      roughness: 0,
      opacity: 1,
      version: stamp.version,
      versionNonce: stamp.nonce,
      updatedAt: Date.now(),
      isDeleted: false,
      ...overrides,
    };
  }

  /**
   * Apply a locally-authored change. The caller has already bumped the
   * version via {@link bumpLocal}. Pushes an undo entry so Ctrl+Z works.
   *
   * `pushUndo = false` is used by undo/redo themselves so the user's undo
   * action doesn't become its own new undoable step.
   */
  upsertLocal(element: SceneElement, pushUndo = true): void {
    const before = this.state.elements[element.id] ?? null;
    this.state.elements[element.id] = element;
    this.localDirty.add(element.id);
    if (pushUndo) {
      this.undoStack.push({ elementId: element.id, before, after: element });
      if (this.undoStack.length > SceneStore.MAX_UNDO) this.undoStack.shift();
      // Any new edit invalidates the redo timeline.
      this.redoStack = [];
    }
    this.bumpSceneVersion();
    this.notify();
  }

  /**
   * Drain the "locally authored since last drain" set. Called by the
   * wire bridge on every store notification to pick up exactly the
   * elements it should broadcast.
   */
  consumeLocalDirty(): string[] {
    if (this.localDirty.size === 0) return [];
    const ids = [...this.localDirty];
    this.localDirty.clear();
    return ids;
  }

  /**
   * Bump (version, versionNonce) for a local edit of an existing element.
   * Use this for every mutation of an element the user already sees — the
   * reconciler relies on monotonic version numbers.
   */
  bumpLocal(element: SceneElement): SceneElement {
    const stamp = nextStamp(element.version);
    return { ...element, version: stamp.version, versionNonce: stamp.nonce, updatedAt: Date.now() };
  }

  /** Soft-delete an element as a local edit. Undoable. */
  deleteLocal(id: string): void {
    const existing = this.state.elements[id];
    if (!existing || existing.isDeleted) return;
    const next = this.bumpLocal({ ...existing, isDeleted: true });
    this.upsertLocal(next);
  }

  /**
   * Apply a server-origin element using the LWW reconciler. Does not touch
   * the undo stack — remote edits aren't "yours" to undo. If the incoming
   * element loses the reconciliation it is silently dropped.
   */
  applyRemote(element: SceneElement): boolean {
    const existing = this.state.elements[element.id];
    if (!existing) {
      this.state.elements[element.id] = element;
      this.bumpSceneVersion();
      this.notify();
      return true;
    }
    if (shouldApply(existing, element)) {
      this.state.elements[element.id] = reconcile(existing, element);
      this.bumpSceneVersion();
      this.notify();
      return true;
    }
    return false;
  }

  /** Fold a batch of server elements (initial subscribe / reconnect). */
  applyRemoteBatch(elements: Iterable<SceneElement>): void {
    let changed = false;
    for (const e of elements) {
      const existing = this.state.elements[e.id];
      if (!existing || shouldApply(existing, e)) {
        this.state.elements[e.id] = existing ? reconcile(existing, e) : e;
        changed = true;
      }
    }
    if (changed) {
      this.bumpSceneVersion();
      this.notify();
    }
  }

  // ---- undo / redo ----

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undo(): SceneElement | null {
    const entry = this.undoStack.pop();
    if (!entry) return null;
    // Replay `before`. If the element didn't exist before the edit, we
    // undo by deleting (soft-delete + new version so the wire agrees).
    let replay: SceneElement;
    if (entry.before) {
      replay = this.bumpLocal(entry.before);
    } else {
      const after = entry.after;
      replay = this.bumpLocal({ ...after, isDeleted: true });
    }
    this.state.elements[entry.elementId] = replay;
    this.localDirty.add(entry.elementId);
    this.redoStack.push({ elementId: entry.elementId, before: entry.after, after: replay });
    this.bumpSceneVersion();
    this.notify();
    return replay;
  }

  redo(): SceneElement | null {
    const entry = this.redoStack.pop();
    if (!entry) return null;
    const replay = this.bumpLocal(entry.before!);
    this.state.elements[entry.elementId] = replay;
    this.localDirty.add(entry.elementId);
    this.undoStack.push({ elementId: entry.elementId, before: entry.after, after: replay });
    this.bumpSceneVersion();
    this.notify();
    return replay;
  }
}

// Module-level singleton. One VTT page = one store. Exported for components
// that don't have the canvas ref in scope (e.g. the tool palette).
export const sceneStore = new SceneStore();
